import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { byKey } from "@/lib/platforms";
import { ArrowRight, Compass as CompassIcon } from "lucide-react";

const QUESTIONS = [
  {
    q: "What pulls at you most right now?",
    options: [
      { label: "Something in my body feels off", w: { health: 3 } },
      { label: "My head is loud, or I want to stop something", w: { mental: 3 } },
      { label: "I want to get stronger, or eat better", w: { vitality: 3 } },
      { label: "My nights, or my energy through the day", w: { sleep: 3 } },
      { label: "Cycles, fertility, pregnancy or midlife", w: { reproductive: 3 } },
      { label: "The people in my life, or the lack of them", w: { relationships: 3 } },
      { label: "Nothing yet. I want to stay ahead of it", w: { prevention: 3 } },
    ],
  },
  {
    q: "How long has this been with you?",
    options: [
      { label: "It arrived recently and it worries me", w: { health: 2 } },
      { label: "Weeks, maybe months", w: { mental: 1, sleep: 1 } },
      { label: "As long as I can remember", w: { mental: 2, relationships: 1 } },
      { label: "It comes and goes with life stages", w: { reproductive: 2 } },
      { label: "It is not a problem, it is a plan", w: { prevention: 2, vitality: 1 } },
    ],
  },
  {
    q: "What would a good outcome look like?",
    options: [
      { label: "An answer I can bring to a doctor", w: { health: 2, prevention: 1 } },
      { label: "A steadier head, day to day", w: { mental: 2 } },
      { label: "A stronger, more capable body", w: { vitality: 2 } },
      { label: "Real nights, real mornings", w: { sleep: 2 } },
      { label: "Understanding my stage of life", w: { reproductive: 2 } },
      { label: "People closer than they are now", w: { relationships: 2 } },
      { label: "Knowing I did the right things early", w: { prevention: 2 } },
    ],
  },
];

export default function Compass({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({});
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const reset = () => { setStep(0); setScores({}); setResult(null); };

  const pick = (w) => {
    const next = { ...scores };
    Object.entries(w).forEach(([k, v]) => { next[k] = (next[k] || 0) + v; });
    if (step + 1 < QUESTIONS.length) {
      setScores(next);
      setStep(step + 1);
    } else {
      const sorted = Object.entries(next).sort((a, b) => b[1] - a[1]);
      setResult({ top: sorted[0][0], second: sorted[1] ? sorted[1][0] : null });
    }
  };

  const go = (key) => {
    onOpenChange(false);
    reset();
    navigate(byKey(key).path);
  };

  const top = result ? byKey(result.top) : null;
  const second = result?.second ? byKey(result.second) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[var(--paper)] border-[var(--line)] max-w-lg" data-testid="compass-dialog">
        <DialogTitle className="sr-only">Take a bearing</DialogTitle>
        <DialogDescription className="sr-only">Three questions that point you to the right of the seven places.</DialogDescription>
        {!result ? (
          <div>
            <div className="flex items-center gap-2 kicker text-[var(--ink-soft)]">
              <CompassIcon size={14} />
              The bearing, {step + 1} of {QUESTIONS.length}
            </div>
            <h3 className="font-display text-2xl sm:text-3xl mt-3">{QUESTIONS[step].q}</h3>
            <div className="mt-5 space-y-2">
              {QUESTIONS[step].options.map((o, i) => (
                <button
                  key={i}
                  onClick={() => pick(o.w)}
                  className="w-full text-left rounded-2xl border border-[var(--line)] px-4 py-3 hover:bg-white/70 hover:translate-x-1 transition-all text-sm sm:text-base"
                  data-testid={`compass-option-${step}-${i}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="mt-5 flex gap-1.5">
              {QUESTIONS.map((_, i) => (
                <span key={i} className="h-1 flex-1 rounded-full" style={{ background: i <= step ? "var(--ink)" : "var(--line)" }} />
              ))}
            </div>
          </div>
        ) : (
          <div data-testid="compass-result">
            <p className="kicker text-[var(--ink-soft)]">Your bearing points to</p>
            <div className="mt-4 rounded-3xl p-6 text-white grain relative overflow-hidden" style={{ background: top.hue }}>
              <p className="font-mono-nc text-xs opacity-80">{top.num}</p>
              <h3 className="font-display text-3xl mt-1">{top.name}</h3>
              <p className="mt-2 text-sm opacity-90 max-w-sm">{top.intro}</p>
              <button
                onClick={() => go(top.key)}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold"
                style={{ color: top.deep }}
                data-testid="compass-go-btn"
              >
                Step inside <ArrowRight size={15} />
              </button>
            </div>
            {second && (
              <button onClick={() => go(second.key)} className="mt-4 text-sm text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors" data-testid="compass-second-btn">
                Or maybe {second.name}? Have a look there instead.
              </button>
            )}
            <button onClick={reset} className="block mt-2 text-xs text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors" data-testid="compass-retry-btn">
              Take the bearing again
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
